import React, { useState, useCallback } from "react";
import {
  Card,
  Input,
  Headline,
  InputLabel,
  Flex,
  Paragraph,
  Button,
} from "_styleguide";
import Footer from "../_components/Footer/Footer";
import VerifyPhone from "./_components/VerifyPhone/VerifyPhone";
import { useLead } from "_contexts/useLead";
import { useSettings } from "_contexts/useSettings";
import { useNavigate } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { FormatPhone } from "_assets/js/helpers";
import { validatePhoneForm } from "./_helpers";
import { seSendPhoneVerification } from "_graphql/mutations";
import { message } from "@centrate-io/barn";
import { isDarkColor } from "_helpers/colors";
import { useStats } from "_contexts/useStats";
import { useRecaptcha } from "_hooks/useRecaptcha";
import { useGTM } from "_hooks/useGTM";
import "./Phone.scss";

function Phone(props) {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { lead, saveLead } = useLead();
  const { settings, isPreview } = useSettings();
  const { updateStats, visitorId } = useStats();
  const { gtmEvent, gtmError } = useGTM();
  const pageSettings = props.settings?.settings;

  const [step, setStep] = useState("input"); // 'input' or 'verify'
  const [maskedDestination, setMaskedDestination] = useState("");
  const [sending, setSending] = useState(false);
  const [phone, setPhone] = useState(lead?.phone || ""); // Local phone state

  // Initialize reCAPTCHA if captcha is enabled for phone page
  const captchaEnabled = pageSettings?.captcha === true;
  const { executeRecaptcha, isLoading: recaptchaLoading } =
    useRecaptcha(captchaEnabled);

  const validation = validatePhoneForm({ phone });
  const validatePhone = pageSettings?.validate_phone;

  const primaryColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryColor) ? "#fff" : "#000";

  // Get application ID from localStorage (source of truth)
  const getApplicationId = () => lead?.id || localStorage.getItem("pp_lead");

  const handleSendVerification = async () => {
    if (isPreview) {
      navigate(`/${props.settings.next}`);
      return;
    }

    setSending(true);

    try {
      // Get recaptcha token if captcha is enabled
      let recaptchaToken = null;
      if (captchaEnabled) {
        recaptchaToken = await executeRecaptcha("phone_verification");
      }

      let leadId = getApplicationId();

      // Create application if we don't have one yet (without phone)
      // Note: Don't pass recaptcha token here - it will be verified in seSendPhoneVerification
      if (!leadId) {
        leadId = await saveLead();
        if (!leadId) {
          throw new Error("Failed to save application");
        }
        localStorage.setItem("pp_lead", leadId);
      }

      // Track stats
      await updateStats({
        application_id: leadId,
        visitor_id: visitorId,
        self_enrollment_id: settings.id,
        page: props?.settings?.name,
        drop_off_page: props?.settings?.name,
        link_visitor_to_application: leadId && visitorId ? true : false,
      });

      // Send verification code (phone not saved to DB yet)
      const variables = {
        application_id: leadId,
        phone: phone,
      };
      // Include recaptcha token if available
      if (recaptchaToken) {
        variables.recaptcha_token = recaptchaToken;
      }

      const result = await client.mutate({
        mutation: seSendPhoneVerification,
        variables,
      });

      const send = result?.data?.seSendPhoneVerification;

      if (send?.success) {
        gtmEvent("phone_verification_sent");
        setMaskedDestination(send.masked_destination);
        setStep("verify");
      } else {
        gtmError("phone_verification_send_failed", {
          error: send?.error,
        });
        message.error(send?.error || "Failed to send verification code");
      }
    } catch (err) {
      console.error("Send verification error:", err);
      gtmError("phone_verification_error");
      message.error("Something went wrong. Please try again.");
    }

    setSending(false);
  };

  const handleVerificationSuccess = async () => {
    gtmEvent("phone_verification_success");
    // Save phone to lead only after verification succeeds
    await saveLead({ phone });

    // Use localStorage directly to avoid stale closure issues
    const appId = localStorage.getItem("pp_lead");

    await updateStats({
      application_id: appId,
      visitor_id: visitorId,
      self_enrollment_id: settings.id,
      page: props?.settings?.name,
      drop_off_page: props?.settings?.next || props?.settings?.name,
    });

    navigate(`/${props.settings.next}`);
  };

  // Function to get recaptcha token - passed to Footer when validate_phone is false
  const getRecaptchaToken = useCallback(async () => {
    if (!captchaEnabled) return null;
    return await executeRecaptcha("phone_submit");
  }, [captchaEnabled, executeRecaptcha]);

  // If verification is enabled and we're on the verify step
  if (validatePhone && step === "verify") {
    return (
      <VerifyPhone
        applicationId={getApplicationId()}
        phone={phone}
        maskedDestination={maskedDestination}
        onSuccess={handleVerificationSuccess}
        pageSettings={pageSettings}
      />
    );
  }

  // Default phone input view
  return (
    <Card id="phone">
      <div className="apply-form">
        <Headline>{pageSettings?.title}</Headline>
        {pageSettings?.subtitle ? (
          <Paragraph
            opacity={0.6}
            align="left"
            size="14px"
            dangerouslySetInnerHTML={{
              __html: pageSettings?.subtitle,
            }}
          />
        ) : null}
        <Flex
          vertical
          style={{ marginTop: pageSettings?.subtitle ? "0" : "24px" }}
        >
          <InputLabel htmlFor="phone" {...validation.phone}>
            Phone:
          </InputLabel>
          <Input
            id="phone"
            value={phone}
            onChange={(e) => setPhone(FormatPhone(e.target.value))}
            size="medium"
            placeholder="e.g. 123-456-7890"
            autoFocus
            {...validation.phone}
          />
        </Flex>
      </div>
      {validatePhone ? (
        <Flex id="footer" align="center">
          <Button
            block
            size="large"
            style={{
              background: primaryColor,
              color: primaryFontColor,
            }}
            onClick={handleSendVerification}
            loading={sending}
            disabled={!validation.allValid || recaptchaLoading}
          >
            Continue
          </Button>
        </Flex>
      ) : (
        <Footer
          next={props.settings.next}
          settings={props.settings}
          disableNext={!validation.allValid || recaptchaLoading}
          getRecaptchaToken={captchaEnabled ? getRecaptchaToken : undefined}
        />
      )}
    </Card>
  );
}

export default Phone;
